/**
 * @lines98/client/services/leaderboard-prefetch — РАННИЙ прогрев + дедуп in-flight
 * страниц лидерборда, чтобы первый заход рисовался ГОТОВЫМ (instant-ui-hydration §4).
 * Ветка над общим «стволом» `lib/shared-flight` (тот же движок дедупа, что у меты).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (instant-hydration на лидерборд+профиль 2026-06-10):
 * `lib/leaderboard-snapshot` убрал «вщёлкивание» строк на ПОВТОРНЫХ заходах (синхронный
 * кэш страницы), но на ПЕРВОМ (холодный кэш) строки всё ещё ждут сеть. Этот модуль —
 * точка раннего прогрева: на boot фоном тянем СТАРТОВУЮ страницу борда (Доска дня →
 * Сегодня — дефолтная посадка экрана) и кладём её в снимок. К моменту входа на лидерборд
 * страница обычно уже прилетела → даже первый заход рисуется из свежего снимка.
 *
 * **Дедуп ПО КЛЮЧУ страницы (`shared-flight`).** Разные вкладки/режимы — разные полёты:
 * boot-префетч «Сегодня» + mount-fetch «Сегодня» дедупятся в ОДИН запрос; переключение на
 * «Вчера»/режим — свой полёт. После ресолва дедуп ключа очищается → следующий fetch той же
 * вкладки (ресинк / повторный заход) летит заново (server-truth, не залипает).
 *
 * **Server-truth/анти-чит цел:** это просто более ранний/недублированный легальный GET
 * (`/daily/leaderboard` / `/alltime/leaderboard` / `/master-jeweler/leaderboard`). Снимок —
 * UX-проекция; `short_id`-видимость решает СЕРВЕР (anti-shadow-practice §3A.4). Ничего не
 * верифицируется и не пишется; движок/replayScore не затронуты. Лидерборд read-only.
 *
 * **Гость:** борд публичный — префетч «Сегодня» работает и без identity, НО снимок keyed
 * по playerId (ствол) → гость кэш не пишет (no-op без ключа). Дедуп всё равно экономит
 * параллельный дубль на mount.
 *
 * Cross-references:
 *   - lib/shared-flight.ts (ствол: дедуп in-flight по ключу).
 *   - lib/leaderboard-snapshot.ts (снимок страницы — кладём прогретую «Сегодня»).
 *   - services/leaderboard-api.ts / meta-api.ts (легальные GET'ы борда — server-truth).
 *   - services/daily-api.ts `prefetchDailySeed` (прецедент: прогрев на boot, errors swallowed).
 */

import { createSharedFlight } from '../lib/shared-flight.js';
import { dailyPaneKey, saveLeaderboardPane, type CachedRowVm } from '../lib/leaderboard-snapshot.js';
import { dateOfDayFromUtc } from '@lines98/shared/util';

import { getCachedDailySeed } from './daily-api.js';
import {
  getDailyLeaderboard,
  getAllTimeLeaderboard,
  LEADERBOARD_PAGE_SIZE,
  ALL_TIME_LEADERBOARD_PAGE_SIZE,
  type LeaderboardResult,
  type LeaderboardRow,
} from './leaderboard-api.js';
import {
  getMasterJewelerLeaderboard,
  type MasterJewelerLeaderboardResult,
} from './meta-api.js';
import type { GameMode } from '@lines98/shared/types/game';

// =============================================================================
// Pane fetch descriptors — какой запрос для какого ключа страницы.
// =============================================================================

/**
 * Описание запроса одной страницы борда: ключ кэша (`PaneKey` экрана) + функция fetch'а.
 * Экран строит дескриптор для активной вкладки и зовёт `sharedLeaderboardPane` — дедуп
 * и прогрев работают по тому же `key`, что и снимок.
 */
export type PaneFetch =
  | { readonly key: string; readonly kind: 'daily'; readonly date: string }
  | { readonly key: string; readonly kind: 'alltime'; readonly mode: GameMode }
  | { readonly key: string; readonly kind: 'karat' };

/** Результат полёта страницы — score-источник (daily/alltime) ИЛИ karat-источник. */
export type PaneResult =
  | { readonly kind: 'score'; readonly result: LeaderboardResult }
  | { readonly kind: 'karat'; readonly result: MasterJewelerLeaderboardResult };

// =============================================================================
// Dedup (ствол) — один полёт на ключ страницы (boot-префетч + mount = один запрос)
// =============================================================================

/** Запоминаем дескриптор по ключу, чтобы `shared-flight` мог вызвать нужный fetch. */
const descriptors = new Map<string, PaneFetch>();

const flight = createSharedFlight<PaneResult>(async (key) => {
  const desc = descriptors.get(key);
  if (!desc) {
    // Не должно случаться (всегда регистрируем перед shared); defensive — пустой score.
    return { kind: 'score', result: { ok: false, error: 'network' } };
  }
  if (desc.kind === 'karat') {
    return { kind: 'karat', result: await getMasterJewelerLeaderboard({ limit: ALL_TIME_LEADERBOARD_PAGE_SIZE }) };
  }
  if (desc.kind === 'alltime') {
    return {
      kind: 'score',
      result: await getAllTimeLeaderboard({ mode: desc.mode, limit: ALL_TIME_LEADERBOARD_PAGE_SIZE }),
    };
  }
  return {
    kind: 'score',
    result: await getDailyLeaderboard({ date: desc.date, limit: LEADERBOARD_PAGE_SIZE }),
  };
});

/**
 * Запросить страницу борда ИЛИ переиспользовать уже летящий полёт того же `key` (дедуп).
 * Экран зовёт это при mount/переключении вместо прямого API — переиспользует boot-префетч
 * (без дубль-запроса). После ресолва дедуп ключа очищается (ресинк летит заново).
 */
export function sharedLeaderboardPane(desc: PaneFetch): Promise<PaneResult> {
  descriptors.set(desc.key, desc);
  return flight.shared(desc.key);
}

// =============================================================================
// Boot prefetch — прогреть СТАРТОВУЮ страницу (Доска дня → Сегодня) + положить в снимок
// =============================================================================

/**
 * Fire-and-forget прогрев стартовой страницы борда на boot (как `prefetchDailySeed`):
 * Доска дня → «Сегодня» (дефолтная посадка экрана). При успехе кладёт строки в снимок
 * (`saveLeaderboardPane` сам no-op для гостя без ключа) — к первому заходу страница свежая.
 *
 * **Ключ ДАТИРОВАН (`daily:<date_of_day>`), НЕ `daily:today` (rollover-fix 2026-06-11).**
 * Раньше прогрев писал снимок под НЕ-датированным ключом `daily:today`; экран читал тот же
 * ключ → после смены дня вчерашний снимок подставлялся на новую доску (owner-playtest баг:
 * лидерборд «Сегодня» показывал вчерашний день, помогал только хард-ресет). Теперь ключ
 * несёт ФАКТИЧЕСКУЮ дату → на новый день ключ ДРУГОЙ → cache miss → свежий fetch; вчерашний
 * снимок (под вчерашней датой) не подставляется. Дату берём СВЕЖЕЙ: `dateOfDayFromUtc(now)`,
 * либо тёплый seed-кэш (он сам само-инвалидируется на rollover — `getCachedDailySeed`).
 *
 * Best-effort: ошибка/offline проглатывается и НИКОГДА не ломает boot — снимок остаётся
 * холодным, экран идёт обычным путём (скелет → fade / «Загружаем…»). Server-truth цел.
 *
 * @param now — текущий local epoch ms (по умолчанию `Date.now()`; инжектируемые часы для тестов).
 */
export function prefetchLeaderboard(now: number = Date.now()): void {
  // День «Сегодня» — из тёплого seed-кэша (daily-api, само-инвалидируется на rollover),
  // иначе из UTC-времени СВЕЖИМ расчётом (тот же расчёт, что экран — единый источник дня
  // борда). Ключ страницы ДАТИРОВАН — `daily:<date_of_day>` (rollover-fix).
  const todayDateOfDay = getCachedDailySeed(now)?.dateOfDay ?? dateOfDayFromUtc(now);
  const key = dailyPaneKey(todayDateOfDay);
  void sharedLeaderboardPane({ key, kind: 'daily', date: todayDateOfDay })
    .then((paneRes) => {
      if (paneRes.kind !== 'score' || !paneRes.result.ok) return;
      saveLeaderboardPane(key, { kind: 'score', rows: paneRes.result.rows.map(toCachedRow) });
    })
    .catch(() => {
      /* best-effort прогрев — offline / ошибка ок, снимок остаётся холодным */
    });
}

/** Спроецировать server-truth строку на кэшируемую (подмножество видимых полей). */
function toCachedRow(r: LeaderboardRow): CachedRowVm {
  return {
    rank: r.rank,
    playerName: r.playerName,
    playerTag: r.playerTag,
    // display-only level badge (server-truth) — pass through so the warm snapshot
    // already carries it (no badge pop-in on the prefetched first paint).
    level: r.level,
    scoreVerified: r.scoreVerified,
    movesCount: r.movesCount,
    isViewer: r.isOwn,
    // server-truth §6.3-видимость verbatim (present → playable; null → teaser).
    shortId: r.shortId,
  };
}

/** Test seam: сбросить дедуп + дескрипторы между кейсами (не трогает снимок/сеть). */
export function _resetLeaderboardPrefetchForTests(): void {
  flight.reset();
  descriptors.clear();
}
